// 匯入匯出：.xmind／.md／Sind .svg 匯入；PNG（前端 canvas）、SVG、Markdown、XMind 匯出；分享連結。
import {
  importXmind, exportXmind, parseOutline, serializeOutline, extractSource, layoutMap, renderSvg,
  type Diagnostic, type FontSet, type MindMap,
} from '@sind/core';
import { extractOutline } from './commands.js';

export interface ImportResult {
  map: MindMap;
  diagnostics: Diagnostic[];
  sheets: number;
}

// 瀏覽器的 deflate-raw 解壓。
async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// 解析大綱文字；錯誤時丟出包含診斷的例外。
export function outlineToMap(text: string): { map: MindMap; diagnostics: Diagnostic[] } {
  const { map, diagnostics } = parseOutline(extractOutline(text));
  if (!map || diagnostics.some(item => item.severity === 'error')) {
    throw Object.assign(new Error(diagnostics.map(item => item.message).join('\n')), { diagnostics });
  }
  return { map, diagnostics };
}

// 依副檔名匯入檔案。
export async function importFile(file: File): Promise<ImportResult> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xmind')) {
    const sheets = await importXmind(new Uint8Array(await file.arrayBuffer()), inflate);
    const first = sheets[0]!;
    return { map: first.map, diagnostics: first.diagnostics, sheets: sheets.length };
  }
  const text = await file.text();
  if (name.endsWith('.svg')) {
    const source = extractSource(text);
    if (source === null) throw new Error('This SVG was not made by Sind (no embedded outline).');
    return { ...outlineToMap(source), sheets: 1 };
  }
  return { ...outlineToMap(text), sheets: 1 };
}

// 觸發瀏覽器下載。
export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// 檔名取自中心主題。
export function baseName(map: MindMap): string {
  return map.root.title.replace(/[\\/:*?"<>|]+/g, '').trim().slice(0, 60) || 'sind';
}

// animate 為 true 時輸出動態 SVG（建構動畫＋流動光點）。
export function exportSvgText(map: MindMap, fonts: FontSet, animate = false): string {
  return renderSvg(layoutMap(map, fonts), fonts, { source: serializeOutline(map, { ids: 'none' }), animate });
}

// SVG → PNG：在 canvas 上以 2 倍解析度繪製（文字已是路徑，不需字型）。
export async function exportPng(map: MindMap, fonts: FontSet, scale = 2): Promise<Blob> {
  const svg = renderSvg(layoutMap(map, fonts), fonts);
  const size = /width="(\d+)" height="(\d+)"/.exec(svg)!;
  const width = Number(size[1]);
  const height = Number(size[2]);
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    const canvas = document.createElement('canvas');
    // 瀏覽器 canvas 有尺寸上限，超大圖自動降低倍率。
    const fitted = Math.min(scale, Math.sqrt(60_000_000 / (width * height)), 16384 / width, 16384 / height);
    canvas.width = Math.round(width * fitted);
    canvas.height = Math.round(height * fitted);
    const context = canvas.getContext('2d')!;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('PNG export failed'))), 'image/png'));
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function exportMarkdown(map: MindMap): Blob {
  return new Blob([serializeOutline(map, { ids: 'none' })], { type: 'text/markdown' });
}

// 給 Claude Code 的文字：說明＋大綱（放在 markdown 區塊，回覆的大綱可直接貼回編輯器）。
export function claudeBrief(map: MindMap, intro: string): string {
  return `${intro}

\`\`\`markdown
${serializeOutline(map, { ids: 'none' }).trimEnd()}
\`\`\`
`;
}

export function exportXmindBlob(map: MindMap): Blob {
  return new Blob([exportXmind(map) as BlobPart], { type: 'application/vnd.xmind.workbook' });
}

// 建立分享連結（伺服器保存一份快照）。
export async function share(map: MindMap): Promise<string> {
  const response = await fetch('/api/share', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ markdown: serializeOutline(map, { ids: 'none' }) }),
  });
  const data = await response.json().catch(() => ({})) as { url?: string; error?: string };
  if (!response.ok || !data.url) throw new Error(data.error ?? `HTTP ${response.status}`);
  return data.url;
}
